'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  AiChat01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  FlowConnectionIcon,
  Megaphone01Icon,
  Refresh01Icon,
  Rocket01Icon,
  Upload01Icon,
  UserGroup03Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { useChat } from '@/contexts/ChatContext'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { FALLBACK_CUSTOMERS } from '@/lib/customers/fallback-data'
import { useCachedUser } from '@/hooks/useCachedUser'
import { DemoDataBadge } from '@/components/DemoDataBadge'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CustomersTable } from '@/components/customers/CustomersTable'
import { CustomerJourneyFlow } from '@/components/customers/CustomerJourneyFlow'
import { AudienceListRow, type AudienceListItem } from '@/components/customers/AudienceListRow'
import { LookalikeAudienceModal } from '@/components/customers/LookalikeAudienceModal'
import {
  customerStatus,
  daysAgo,
  fmtUsd,
  JOURNEY_CHANNEL_COLOR,
  JOURNEY_CHANNEL_LABEL,
  type Customer,
  type JourneyChannel,
} from '@/lib/customers/customer-row'

type CustomerApiRow = {
  id: string
  anon_id: string
  display_name: string | null
  email: string | null
  phone: string | null
  total_spent_cents: number
  orders_count: number
  first_seen_at: string
  last_seen_at: string
  products: Array<{ name: string; url: string | null }>
}

type CustomersPayload = {
  customers?: CustomerApiRow[]
  next_cursor?: string | null
  total_count?: number
  error?: string
}

type CampaignStatusKind = 'Draft' | 'Scheduled' | 'Live'
type RetargetCampaign = {
  id: string
  name: string
  audience: string
  channels: JourneyChannel[]
  status: CampaignStatusKind
  reach: number
  createdDaysAgo: number
}

const ACTIVATION_CHANNELS: Array<{ key: JourneyChannel; label: string; color: string }> = [
  { key: 'search', label: 'Search', color: JOURNEY_CHANNEL_COLOR.search },
  { key: 'social', label: 'Social', color: JOURNEY_CHANNEL_COLOR.social },
  { key: 'tv', label: 'TV / CTV', color: JOURNEY_CHANNEL_COLOR.tv },
  { key: 'email', label: 'Email', color: JOURNEY_CHANNEL_COLOR.email },
]

const RETARGET_CAMPAIGNS: RetargetCampaign[] = [
  { id: 'rc1', name: 'Cart abandoners - Meta + Google', audience: 'Cart abandoners', channels: ['social', 'search'], status: 'Draft', reach: 0, createdDaysAgo: 0 },
]

const CAMPAIGN_STATUS_STYLE: Record<CampaignStatusKind, string> = {
  Live: 'bg-[#0A8A6E]/12 text-[#0A6b54] dark:bg-[#2BC39A]/15 dark:text-[#2BC39A]',
  Scheduled: 'bg-[#2D6CDF]/12 text-[#1d4ed8] dark:bg-[#2D6CDF]/15 dark:text-[#7eb0ff]',
  Draft: 'bg-black/[0.05] text-text dark:bg-white/[0.08]',
}

export default function CustomersPage() {
  const router = useRouter()
  const { openChat } = useChat()
  const { isLoggedIn, isLoading: authLoading } = useCachedUser()
  const fileRef = useRef<HTMLInputElement>(null)
  const [tab, setTab] = useState<'customers' | 'campaigns'>('customers')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [modalCustomer, setModalCustomer] = useState<Customer | null>(null)
  const [lookalikeOpen, setLookalikeOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [campaigns, setCampaigns] = useState<RetargetCampaign[]>(RETARGET_CAMPAIGNS)
  const [cName, setCName] = useState('')
  const [cChannels, setCChannels] = useState<JourneyChannel[]>([])

  const totals = useMemo(() => {
    const revenueCents = customers.reduce((sum, customer) => sum + customer.totalSpentCents, 0)
    const identified = customers.filter((customer) => customer.email || customer.phone).length
    return { revenueCents, identified }
  }, [customers])

  async function loadCustomers(cursor?: string | null, append = false) {
    setLoading(true)
    const url = new URL('/api/customers', window.location.origin)
    url.searchParams.set('limit', '100')
    url.searchParams.set('sort', 'anon_id')
    url.searchParams.set('dir', 'asc')
    if (cursor) url.searchParams.set('cursor', cursor)

    const res = await fetch(url)
    const payload = (await res.json().catch(() => null)) as CustomersPayload | null
    setLoading(false)

    if (!res.ok) {
      toast.error(payload?.error ?? 'Unable to load customers.')
      return
    }

    const mapped = (payload?.customers ?? []).map(mapCustomer)
    setCustomers((current) => append ? [...current, ...mapped] : mapped)
    setTotalCount(payload?.total_count ?? mapped.length)
    setNextCursor(payload?.next_cursor ?? null)
  }

  useEffect(() => {
    if (authLoading) return
    if (!isLoggedIn) {
      setCustomers(FALLBACK_CUSTOMERS)
      setTotalCount(FALLBACK_CUSTOMERS.length)
      setLoading(false)
      return
    }
    void loadCustomers(null, false)
  }, [authLoading, isLoggedIn])

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return
    const fd = new FormData()
    fd.append('file', files[0])
    toast.loading('Uploading customers...', { id: 'csv-upload' })

    const res = await fetch('/api/customers/upload', { method: 'POST', body: fd })
    const data = await res.json().catch(() => null) as { inserted?: number; updated?: number; error?: string } | null
    if (!res.ok) {
      toast.error(data?.error ?? 'Upload failed. Check the CSV format.', { id: 'csv-upload' })
      return
    }

    toast.success(`${data?.inserted ?? 0} new + ${data?.updated ?? 0} updated`, { id: 'csv-upload' })
    await loadCustomers(null, false)
    router.refresh()
  }

  async function importFromGA() {
    // removed from UI — kept to avoid breaking prop types during cleanup
    void 0
    router.refresh()
  }

  function submitCampaign(status: CampaignStatusKind) {
    if (!cName.trim()) return toast.error('Name your campaign.')
    if (cChannels.length === 0) return toast.error('Pick at least one channel.')

    const created: RetargetCampaign = {
      id: `rc-new-${campaigns.length + 1}`,
      name: cName.trim(),
      audience: 'First-party customers',
      channels: cChannels,
      status,
      reach: totalCount,
      createdDaysAgo: 0,
    }
    setCampaigns((current) => [created, ...current])
    setCreateOpen(false)
    setCName('')
    setCChannels([])
    setTab('campaigns')
    toast.success(status === 'Live' ? `"${created.name}" launched.` : `"${created.name}" saved as a draft.`)
  }

  return (
    <div className="min-h-full dark:bg-canvas">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.tsv,.txt"
        className="hidden"
        onChange={(event) => {
          void handleUpload(event.target.files)
          event.target.value = ''
        }}
      />

      <LobbyHeaderMeta>{totalCount.toLocaleString()} customers</LobbyHeaderMeta>

      <div className="px-4 pb-16 pt-6 sm:px-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="m-0 text-[28px] font-bold tracking-[-0.02em] text-text-strong">
                Customers
              </h1>
              {!authLoading && !isLoggedIn && <DemoDataBadge />}
            </div>
            <p className="mt-1 max-w-[480px] text-[13.5px] text-text">
              Build, sync, and monitor first-party audiences across every connected platform.
            </p>
            <TooltipProvider>
              <div className="mt-3.5 flex flex-wrap gap-2.5">
                <Tooltip>
                  <TooltipTrigger asChild><span><Kpi label="Customers" value={totalCount.toLocaleString()} /></span></TooltipTrigger>
                  <TooltipContent>Total unique first-party customers in your workspace.</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild><span><Kpi label="Identified" value={`${totals.identified}/${totalCount}`} accent="#0A8A6E" /></span></TooltipTrigger>
                  <TooltipContent>Customers with at least one resolved identifier.</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild><span><Kpi label="Attributed revenue" value={fmtUsd(totals.revenueCents)} accent="#0A8A6E" /></span></TooltipTrigger>
                  <TooltipContent>Total revenue from imported customer records.</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-[11px] border border-black/10 bg-white px-[14px] py-2.5 text-[13px] font-bold text-text-strong transition-colors hover:bg-black/[0.03] dark:border-white/10 dark:bg-surface dark:hover:bg-white/[0.05]"
                >
                  <HugeiconsIcon icon={Megaphone01Icon} className="size-4 text-[#0A8A6E]" />
                  New campaign
                  <HugeiconsIcon icon={ArrowDown01Icon} className="size-3.5 text-[#939294]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setCreateOpen(true)} className="gap-2">
                  <HugeiconsIcon icon={Rocket01Icon} className="size-4 text-[#0A8A6E]" />
                  Retargeting campaign
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLookalikeOpen(true)} className="gap-2">
                  <HugeiconsIcon icon={UserGroup03Icon} className="size-4 text-[#0A8A6E]" />
                  Lookalike audiences
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-[11px] bg-[#0A8A6E] px-[14px] py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#0a7a62]"
            >
              <HugeiconsIcon icon={Upload01Icon} className="size-4" />
              Upload list
            </button>
          </div>
        </div>

        <div className="mt-5 flex gap-6 border-b border-black/[0.08] dark:border-white/[0.08]">
          {([
            { key: 'customers', label: 'Customers', count: totalCount },
            { key: 'campaigns', label: 'Campaigns', count: campaigns.length },
          ] as const).map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[14px] font-semibold transition-colors ${
                tab === item.key
                  ? 'border-[#0A8A6E] text-text-strong'
                  : 'border-transparent text-[#939294] hover:text-text-strong'
              }`}
            >
              {item.label}
              {item.count !== null && <span className="font-mono text-[11px] text-[#939294]">{item.count}</span>}
            </button>
          ))}
        </div>

        {tab === 'customers' ? (
          <CustomersTab
            customers={customers}
            loading={loading}
            nextCursor={nextCursor}
            onLoadMore={() => void loadCustomers(nextCursor, true)}
            onOpen={(id) => router.push(`/customers/${id}`)}
            onView={(customer) => setModalCustomer(customer)}
            onUpload={() => fileRef.current?.click()}
            onImportGA={() => void importFromGA()}
          />
        ) : (
          <CampaignsTab campaigns={campaigns} onCreate={() => setCreateOpen(true)} />
        )}
      </div>

      <Dialog open={!!modalCustomer} onOpenChange={(open) => !open && setModalCustomer(null)}>
        <DialogContent showCloseButton={false} className="overflow-hidden rounded-2xl border-0 bg-white p-0 dark:bg-surface sm:max-w-[640px]">
          {modalCustomer && (
            <>
              <div className="relative bg-[linear-gradient(135deg,#006241,#2BC39A)] px-6 pb-5 pt-6 text-white">
                <DialogTitle className="font-mono text-[15px] font-extrabold tracking-[-0.01em] text-white">
                  {modalCustomer.anonId}
                </DialogTitle>
                <p className="mt-1 text-[12.5px] text-white/85">
                  {modalCustomer.email ?? 'No email'} · {modalCustomer.phone ?? 'No phone'}
                </p>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setModalCustomer(null)}
                  className="absolute right-4 top-4 grid size-7 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25"
                >
                  <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
                </button>
              </div>
              <div className="px-5 py-4">
                <div className="mb-1 text-[12px] font-semibold uppercase tracking-[0.06em] text-[#939294]">
                  Path to purchase
                </div>
                <CustomerJourneyFlow customer={modalCustomer} size="md" />
                <div className="mt-3 flex items-center justify-between border-t border-black/[0.06] pt-3 dark:border-white/[0.08]">
                  <span className="text-[12.5px] text-text">
                    {modalCustomer.journey.length} touchpoints · {fmtUsd(modalCustomer.totalSpentCents)} ·{' '}
                    {modalCustomer.ordersCount} order{modalCustomer.ordersCount === 1 ? '' : 's'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const id = modalCustomer.id
                      setModalCustomer(null)
                      router.push(`/customers/${id}`)
                    }}
                    className="inline-flex items-center gap-1 rounded-lg bg-[#0A8A6E] px-3 py-1.5 text-[12.5px] font-bold text-white hover:bg-[#0a7a62]"
                  >
                    Full profile
                    <HugeiconsIcon icon={ArrowRight01Icon} className="size-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) setCreateOpen(false) }}>
        <DialogContent showCloseButton={false} className="overflow-hidden rounded-2xl border-0 bg-white p-0 dark:bg-surface sm:max-w-[520px]">
          <div className="relative bg-[linear-gradient(135deg,#006241,#2BC39A)] px-6 pb-5 pt-6 text-white">
            <DialogTitle className="text-[18px] font-extrabold tracking-[-0.01em] text-white">
              Create retargeting campaign
            </DialogTitle>
            <button
              type="button"
              aria-label="Continue in chat"
              title="Continue in chat"
              onClick={() => {
                setCreateOpen(false)
                openChat('campaign', { seedMessage: 'Help me build a retargeting campaign from first-party customers.' })
              }}
              className="absolute right-[52px] top-4 grid size-7 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25"
            >
              <HugeiconsIcon icon={AiChat01Icon} className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setCreateOpen(false)}
              className="absolute right-4 top-4 grid size-7 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25"
            >
              <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
            </button>
          </div>
          <div className="px-5 py-4">
            <label className="block text-[12px] font-semibold text-text-strong">Campaign name</label>
            <input
              value={cName}
              onChange={(event) => setCName(event.target.value)}
              placeholder="e.g. First-party customer win-back"
              className="mt-1.5 h-11 w-full rounded-xl border-[1.5px] border-black/10 bg-[#FAFAF7] px-3 text-sm text-text-strong outline-none placeholder:text-[#b6b4ac] focus:border-[#0A8A6E] dark:border-white/10 dark:bg-atrium-inner dark:placeholder:text-atrium-muted"
            />
            <div className="mt-4 text-[12px] font-semibold text-text-strong">Channels</div>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {ACTIVATION_CHANNELS.map((channel) => {
                const selected = cChannels.includes(channel.key)
                return (
                  <button
                    key={channel.key}
                    type="button"
                    onClick={() => setCChannels((current) => selected ? current.filter((item) => item !== channel.key) : [...current, channel.key])}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                      selected
                        ? 'text-white'
                        : 'border-black/[0.12] bg-white text-text-strong dark:border-white/[0.12] dark:bg-atrium-inner'
                    }`}
                    style={selected ? { background: channel.color, borderColor: channel.color } : undefined}
                  >
                    {selected && <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-3.5" />}
                    {channel.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-black/[0.06] px-5 py-4 dark:border-white/[0.08]">
            <button type="button" onClick={() => submitCampaign('Draft')} className="rounded-[11px] border border-black/10 bg-white px-[15px] py-2.5 text-[13px] font-bold text-text-strong hover:bg-black/[0.03] dark:border-white/10 dark:bg-atrium-inner dark:hover:bg-white/[0.05]">
              Save for later
            </button>
            <button type="button" onClick={() => submitCampaign('Live')} className="inline-flex items-center gap-1.5 rounded-[11px] bg-[#0A8A6E] px-[15px] py-2.5 text-[13px] font-bold text-white hover:bg-[#0a7a62]">
              <HugeiconsIcon icon={Rocket01Icon} className="size-4" />
              Launch campaign
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <LookalikeAudienceModal
        open={lookalikeOpen}
        onOpenChange={setLookalikeOpen}
        customers={customers}
        onCreated={() => {}}
      />
    </div>
  )
}

function mapCustomer(row: CustomerApiRow): Customer {
  return {
    id: row.id,
    anonId: row.anon_id,
    displayName: row.display_name,
    email: row.email,
    phone: row.phone,
    status: customerStatus(row.total_spent_cents, row.orders_count),
    channels: ['direct'],
    products: row.products.map((product) => ({ name: product.name, url: product.url, priceCents: 0 })),
    journey: [{ channel: 'direct', label: 'First-party import', day: 0 }],
    totalSpentCents: row.total_spent_cents,
    ordersCount: row.orders_count,
    firstSeenDaysAgo: daysAgo(row.first_seen_at),
    firstSeenAt: row.first_seen_at,
    lastSeenAt: row.last_seen_at,
  }
}

function CustomersTab({
  customers,
  loading,
  nextCursor,
  onLoadMore,
  onOpen,
  onView,
  onUpload,
  onImportGA,
}: {
  customers: Customer[]
  loading: boolean
  nextCursor: string | null
  onLoadMore: () => void
  onOpen: (id: string) => void
  onView: (customer: Customer) => void
  onUpload: () => void
  onImportGA: () => void
}) {
  if (loading && customers.length === 0) {
    return <div className="mt-4 rounded-2xl border border-black/[0.06] bg-white p-5 text-[13px] text-text dark:border-white/[0.08] dark:bg-surface">Loading customers...</div>
  }

  if (customers.length === 0) {
    return (
      <div className="mt-5 flex min-h-[300px] items-center justify-center rounded-2xl border border-black/[0.06] bg-white p-8 text-center dark:border-white/[0.08] dark:bg-surface">
        <div>
          <div className="text-[18px] font-bold text-text-strong">No customers yet.</div>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={onUpload} className="inline-flex items-center gap-1.5 rounded-[11px] bg-[#0A8A6E] px-[14px] py-2.5 text-[13px] font-bold text-white hover:bg-[#0a7a62]">
              <HugeiconsIcon icon={Upload01Icon} className="size-4" />
              Upload CSV
            </button>
          </div>
          <Link href="/marketplace" className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-[#0A6b54] hover:underline">
            Connect a source to start syncing customers automatically
            <ArrowRight01Text />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <CustomersTable customers={customers} onOpenCustomer={onOpen} onViewPath={onView} />
      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button type="button" onClick={onLoadMore} className="inline-flex items-center gap-1.5 rounded-[11px] border border-black/10 bg-white px-3 py-2 text-[12.5px] font-bold text-text-strong transition-colors hover:bg-black/[0.03] dark:border-white/10 dark:bg-surface dark:hover:bg-white/[0.05]">
            <HugeiconsIcon icon={Refresh01Icon} className="size-3.5" />
            Load more
          </button>
        </div>
      )}
      <AudiencesSection />
    </>
  )
}

function ArrowRight01Text() {
  return <span aria-hidden>→</span>
}

function CampaignsTab({ campaigns, onCreate }: { campaigns: RetargetCampaign[]; onCreate: () => void }) {
  return (
    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {campaigns.map((campaign) => (
        <div key={campaign.id} className="rounded-2xl border border-black/[0.06] bg-white p-4 dark:border-white/[0.08] dark:bg-surface">
          <div className="flex items-start justify-between gap-2">
            <div className="text-[14px] font-bold leading-snug text-text-strong">{campaign.name}</div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${CAMPAIGN_STATUS_STYLE[campaign.status]}`}>
              {campaign.status}
            </span>
          </div>
          <div className="mt-2 text-[12.5px] text-text">{campaign.audience}</div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {campaign.channels.map((channel) => (
              <span key={channel} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold text-white" style={{ background: JOURNEY_CHANNEL_COLOR[channel] }}>
                {JOURNEY_CHANNEL_LABEL[channel]}
              </span>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-black/[0.06] pt-3 text-[12px] text-[#939294] dark:border-white/[0.08]">
            <span className="font-mono">{campaign.reach.toLocaleString()} reach</span>
            <span>{campaign.createdDaysAgo === 0 ? 'Just now' : `${campaign.createdDaysAgo}d ago`}</span>
          </div>
        </div>
      ))}
      <button type="button" onClick={onCreate} className="flex min-h-[148px] flex-col items-center justify-center gap-2 rounded-2xl border-[1.5px] border-dashed border-[#0A8A6E]/30 bg-[#0A8A6E]/[0.04] p-4 text-center transition-colors hover:bg-[#0A8A6E]/[0.08] dark:border-[#2BC39A]/30 dark:bg-[#2BC39A]/[0.06] dark:hover:bg-[#2BC39A]/[0.10]">
        <span className="grid size-10 place-items-center rounded-full bg-[#0A8A6E]/12 text-[#0A8A6E] dark:bg-[#2BC39A]/15 dark:text-[#2BC39A]">
          <HugeiconsIcon icon={Rocket01Icon} className="size-5" />
        </span>
        <span className="text-[13px] font-bold text-[#0A6b54] dark:text-[#2BC39A]">Create campaign</span>
        <span className="text-[11.5px] text-text">Retarget a custom audience</span>
      </button>
    </div>
  )
}

function AudiencesSection() {
  const { isLoggedIn, isLoading: authLoading } = useCachedUser()
  const [audiences, setAudiences] = useState<AudienceListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (authLoading) return
    if (!isLoggedIn) {
      setAudiences([])
      setError(null)
      setLoading(false)
      return
    }

    let active = true
    async function loadAudiences() {
      setLoading(true)
      setError(null)
      const res = await fetch('/api/customers/audiences')
      const payload = await res.json().catch(() => null) as { audiences?: AudienceListItem[]; error?: string } | null
      if (!active) return
      setLoading(false)
      if (!res.ok) {
        setError(payload?.error ?? 'Unable to load audiences.')
        return
      }
      setAudiences(payload?.audiences ?? [])
    }
    void loadAudiences()
    return () => { active = false }
  }, [authLoading, isLoggedIn])

  if (loading) {
    return (
      <section className="mt-10">
        <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-text-subtle">Audiences</h3>
        <div className="mt-3 rounded-2xl border border-black/[0.06] bg-white p-5 text-[13px] text-text dark:border-white/[0.08] dark:bg-surface">Loading audiences...</div>
      </section>
    )
  }
  if (error) {
    return (
      <section className="mt-10">
        <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-text-subtle">Audiences</h3>
        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-[13px] font-medium text-red-600 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">{error}</div>
      </section>
    )
  }
  if (audiences.length === 0) {
    return (
      <section className="mt-10">
        <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-text-subtle">Audiences</h3>
        <div className="mt-3 flex min-h-[180px] items-center justify-center rounded-2xl border border-black/[0.06] bg-white p-8 text-center dark:border-white/[0.08] dark:bg-surface">
          <div>
            <div className="text-[16px] font-bold text-text-strong">No audiences yet.</div>
            <p className="mt-2 max-w-[520px] text-[13px] leading-[1.55] text-text">Create a lookalike audience from your first-party customers.</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="mt-10">
      <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-text-subtle">Audiences</h3>
      <div className="mt-3 overflow-hidden rounded-2xl border border-black/[0.06] bg-white dark:border-white/[0.08] dark:bg-surface">
        {audiences.map((audience) => <AudienceListRow key={audience.id} audience={audience} />)}
      </div>
    </section>
  )
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-black/[0.06] bg-white px-3.5 py-2 dark:border-white/[0.08] dark:bg-surface">
      <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#939294]">{label}</div>
      <div
        className={accent ? 'mt-0.5 font-mono text-[18px] font-extrabold tracking-[-0.02em] tabular-nums' : 'mt-0.5 font-mono text-[18px] font-extrabold tracking-[-0.02em] tabular-nums text-text-strong'}
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  )
}
